import cn from "@/utils/cn";

/**
 * "EMAIL AUDIT ENGINE" brand lockup, inlined from eae-icon.svg so it stays the
 * original artwork/typography — but the EMAIL/ENGINE glyphs (originally the dark
 * navy #05004E) are drawn with `currentColor` instead. The SVG's colour is set
 * to kd-secondary-1, which is exactly rgb(5 0 78) = #05004E in light mode (so
 * light is pixel-identical to the original) and white in dark mode (so the
 * wordmark stays legible on the dark background). AUDIT and the envelope keep
 * their baked-in blues. Size it by width, e.g. className="w-[220px]".
 */
const BrandWordmark = ({ className }: { className?: string }) => (
  <svg
    className={cn("h-auto w-[220px] text-kd-secondary-1", className)}
    viewBox="0 0 252 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Email Audit Engine"
  >
    {/* Envelope mark */}
    <path
      d="M35.8408 8.50363C35.8408 8.50363 35.8408 8.44656 35.8472 8.41486C35.8472 8.42754 35.8472 8.44656 35.8408 8.45924V8.50363Z"
      fill="#99A7E0"
    />
    <path
      d="M3.55127 8.45926C3.55127 8.45926 3.55127 8.44023 3.55127 8.42755C3.55127 8.45292 3.55127 8.47194 3.55761 8.4973V8.45292L3.55127 8.45926Z"
      fill="#99A7E0"
    />
    <path
      d="M19.6958 19.7212L14.8765 16.4365L19.7021 19.772L25.2697 15.9229L19.7021 19.7212H19.6958Z"
      fill="#C8CCFC"
    />
    <path
      d="M35.815 8.63679L25.2695 15.9228L35.815 8.73825C35.8277 8.66215 35.8277 8.58606 35.8403 8.50996V8.46558C35.8277 8.52265 35.8277 8.57972 35.815 8.63679Z"
      fill="#FDFDFD"
    />
    <path
      d="M14.8767 16.4365L3.57663 8.63679C3.56395 8.57972 3.56395 8.52265 3.55127 8.46558V8.50996C3.56395 8.58606 3.56395 8.66215 3.57663 8.73825L14.8767 16.4428V16.4365Z"
      fill="#FDFDFD"
    />
    <path
      d="M14.8767 16.4365L19.696 19.7213H19.7024L25.2699 15.9166L35.8154 8.6305C35.8281 8.57343 35.8281 8.51636 35.8408 8.45929H3.55127C3.56395 8.51636 3.56395 8.57343 3.57663 8.6305L14.8767 16.4365Z"
      fill="#FDFDFD"
    />
    <path
      d="M35.8472 8.41485C35.8472 8.41485 35.8472 8.41485 35.8408 8.40851V8.46558C35.8408 8.46558 35.8408 8.43387 35.8472 8.42119V8.41485Z"
      fill="#2B3640"
    />
    <path
      d="M35.8406 30.9452V31.0023C38.0854 27.8 39.4043 23.9065 39.4043 19.7022C39.4043 15.498 38.0917 11.6235 35.8596 8.43389C35.8596 8.43389 35.8532 8.42754 35.8469 8.4212C35.8469 8.45291 35.8469 8.47827 35.8406 8.50998C35.8406 8.50998 35.8406 8.54803 35.8342 8.57339L35.8025 8.75729L25.2761 15.9355L19.7085 19.7847L14.8828 16.4492L3.67791 8.80168L3.55108 8.49096C3.55108 8.49096 3.55108 8.45291 3.54474 8.42754C3.54474 8.42754 3.5384 8.44023 3.53206 8.44657C1.30629 11.6425 0 15.517 0 19.7022C0 23.8874 1.31263 27.7809 3.55108 30.9769V30.9389L19.6831 19.7783H19.6958L35.8406 30.7106V30.9389V30.9452Z"
      fill="#61A1FF"
    />
    <path
      d="M35.7203 8.23724C32.1438 3.25304 26.3036 0 19.7024 0C19.7024 0 19.7024 0 19.696 0C13.1011 0 7.26722 3.2467 3.69078 8.22456C3.64005 8.28797 3.59566 8.35773 3.55127 8.42114V8.45918H35.8408V8.40211C35.8027 8.34504 35.7647 8.28797 35.7203 8.23724Z"
      fill="#61A1FF"
    />
    <path
      d="M19.696 19.772H19.6833L3.55127 30.9452V30.9832C3.59566 31.053 3.64639 31.1164 3.69078 31.1798C7.26722 36.1577 13.0948 39.4044 19.6897 39.4044C19.6897 39.4044 19.6897 39.4044 19.696 39.4044C26.2972 39.4044 32.1375 36.1513 35.7139 31.1671C35.752 31.1101 35.7964 31.053 35.8344 31.0023V30.7169L19.6897 19.7783L19.696 19.772Z"
      fill="#336DFF"
    />
    {/* EMAIL — theme-aware (currentColor = #05004E in light, white in dark) */}
    <path
      d="M51.5796 25.5552V13.8493H60.8568V16.4112H55.0736V18.4213H60.3812V20.9832H55.0736V22.9933H60.8314V25.5552H51.5796Z"
      fill="currentColor"
    />
    <path
      d="M64.2432 13.8493H68.5932L71.5863 20.4822H71.7385L74.7315 13.8493H79.0816V25.5552H75.6637V18.7891H75.5622L72.696 25.4664H70.6351L67.7689 18.7447H67.6674V25.5552H64.2495V13.8493H64.2432Z"
      fill="currentColor"
    />
    <path
      d="M84.9659 25.5552H81.1929L85.4415 13.8493H90.2228L94.4714 25.5552H90.6984L87.8829 17.0706H87.7814L84.9659 25.5552ZM84.262 20.9388H91.3515V23.3167H84.262V20.9388Z"
      fill="currentColor"
    />
    <path
      d="M100.09 13.8493V25.5552H96.5957V13.8493H100.09Z"
      fill="currentColor"
    />
    <path
      d="M103.463 25.5552V13.8493H106.957V22.9933H112.164V25.5552H103.463Z"
      fill="currentColor"
    />
    {/* AUDIT — brand blue */}
    <path
      d="M119.792 25.5552H116.019L120.268 13.8493H125.049L129.298 25.5552H125.525L122.709 17.0706H122.608L119.792 25.5552ZM119.088 20.9388H126.178V23.3167H119.088V20.9388Z"
      fill="#336DFF"
    />
    <path
      d="M138.34 13.8493H141.834V21.351C141.834 22.2451 141.599 23.0124 141.13 23.6655C140.661 24.3123 140.014 24.8133 139.177 25.1684C138.34 25.5235 137.376 25.6947 136.273 25.6947C135.169 25.6947 134.186 25.5171 133.356 25.1684C132.519 24.8133 131.872 24.3123 131.409 23.6655C130.946 23.0187 130.711 22.2451 130.711 21.351V13.8493H134.205V21.072C134.205 21.4334 134.294 21.7568 134.472 22.0422C134.649 22.3275 134.89 22.5495 135.201 22.708C135.512 22.8665 135.867 22.949 136.266 22.949C136.666 22.949 137.027 22.8665 137.338 22.708C137.649 22.5495 137.89 22.3275 138.067 22.0422C138.245 21.7568 138.334 21.4334 138.334 21.072V13.8493H138.34Z"
      fill="#336DFF"
    />
    <path
      d="M149.57 25.5552H144.618V13.8493H149.519C150.845 13.8493 151.993 14.0839 152.956 14.5468C153.92 15.0161 154.669 15.6819 155.195 16.557C155.721 17.4321 155.987 18.4784 155.987 19.6959C155.987 20.9134 155.727 21.9597 155.201 22.8348C154.675 23.7099 153.933 24.3821 152.975 24.845C152.018 25.3142 150.883 25.5425 149.577 25.5425L149.57 25.5552ZM148.112 22.8602H149.443C150.077 22.8602 150.623 22.7651 151.073 22.5812C151.523 22.3973 151.866 22.0739 152.107 21.6173C152.341 21.1607 152.462 20.5266 152.462 19.7086C152.462 18.8906 152.341 18.2565 152.1 17.7999C151.859 17.3433 151.504 17.0199 151.041 16.836C150.578 16.6521 150.014 16.557 149.342 16.557H148.112V22.8665V22.8602Z"
      fill="#336DFF"
    />
    <path
      d="M161.828 13.8493V25.5552H158.334V13.8493H161.828Z"
      fill="#336DFF"
    />
    <path
      d="M163.92 16.4112V13.8493H175.113V16.4112H171.238V25.5552H167.795V16.4112H163.92Z"
      fill="#336DFF"
    />
    {/* ENGINE — theme-aware */}
    <path
      d="M180.68 25.5552V13.8493H189.957V16.4112H184.174V18.4213H189.482V20.9832H184.174V22.9933H189.932V25.5552H180.68Z"
      fill="currentColor"
    />
    <path
      d="M204.327 13.8493V25.5552H201.41L196.756 19.4042H196.68V25.5552H193.186V13.8493H196.153L200.732 19.9749H200.833V13.8493H204.327Z"
      fill="currentColor"
    />
    <path
      d="M215.006 17.7175C214.949 17.5145 214.866 17.337 214.746 17.1848C214.631 17.0326 214.479 16.8994 214.308 16.7916C214.137 16.6838 213.934 16.6014 213.706 16.5443C213.477 16.4873 213.23 16.4619 212.957 16.4619C212.368 16.4619 211.873 16.5887 211.455 16.8424C211.042 17.096 210.725 17.4638 210.51 17.9457C210.294 18.4277 210.186 19.0047 210.186 19.6832C210.186 20.3618 210.288 20.9578 210.497 21.4461C210.7 21.9344 211.011 22.3085 211.416 22.5685C211.822 22.8285 212.33 22.9553 212.932 22.9553C213.465 22.9553 213.909 22.8856 214.264 22.746C214.619 22.6065 214.885 22.4036 215.056 22.1436C215.234 21.8836 215.323 21.5793 215.323 21.2305L215.925 21.2876H212.983V19.0238H218.69V20.6471C218.69 21.7124 218.443 22.6256 217.948 23.3802C217.453 24.1348 216.768 24.7182 215.9 25.1177C215.031 25.5172 214.035 25.7201 212.913 25.7201C211.657 25.7201 210.56 25.4791 209.609 24.9908C208.664 24.5089 207.922 23.8177 207.39 22.9236C206.857 22.0295 206.597 20.9642 206.597 19.7276C206.597 18.7574 206.762 17.9014 207.085 17.1531C207.409 16.4048 207.859 15.777 208.442 15.2571C209.02 14.7434 209.692 14.3503 210.446 14.0839C211.207 13.8176 212.019 13.6844 212.888 13.6844C213.649 13.6844 214.359 13.7796 215.018 13.9761C215.671 14.1727 216.255 14.4517 216.762 14.8132C217.269 15.1746 217.675 15.5995 217.986 16.0878C218.297 16.5824 218.487 17.1214 218.55 17.7048H215.018L215.006 17.7175Z"
      fill="currentColor"
    />
    <path
      d="M224.936 13.8493V25.5552H221.442V13.8493H224.936Z"
      fill="currentColor"
    />
    <path
      d="M239.343 13.8493V25.5552H236.426L231.772 19.4042H231.696V25.5552H228.202V13.8493H231.169L235.748 19.9749H235.849V13.8493H239.343Z"
      fill="currentColor"
    />
    <path
      d="M242.723 25.5552V13.8493H252V16.4112H246.217V18.4213H251.524V20.9832H246.217V22.9933H251.974V25.5552H242.723Z"
      fill="currentColor"
    />
  </svg>
);

export default BrandWordmark;
